/**
 * useCallDataEvents — placeholder hook for call data channel events.
 *
 * **Status (PRD 0 / Story 0.3.2):** placeholder. `emit` is a no-op that
 * logs a warning and resolves with a sentinel UUID; `events` is an
 * empty array (stable reference). No network calls.
 *
 * **What changes in PRD 3 (audio/video infra):** the hook will subscribe
 * to the LiveKit `Room` data channel for the given `callSessionId` and
 * push received events into `events` via internal `useState`. `emit`
 * will dispatch via `room.localParticipant.publishData(...)` and/or
 * `POST /api/v1/call-data-events` (depending on `sentVia`), mirroring
 * the backend `CallDataEventService.emit()` contract.
 *
 * The shape `{ emit, events }` and the exported types are **frozen**
 * here so that components written in PRDs 1-2 (annotation toolbar,
 * sentiment badge, supervisor join button, watermark overlay) compile
 * against the final API today and require zero changes when PRD 3
 * lights up the real transport.
 *
 * @example
 * ```tsx
 * const { emit, events } = useCallDataEvents(callSessionId);
 *
 * // Annotation: low-latency peer broadcast, no audit needed
 * await emit({
 *   eventType: "annotation",
 *   payload: { x: 42, y: 100, color: "red" },
 *   sentVia: "data_channel",
 * });
 *
 * // Sentiment alert: peer + audit + rule trigger
 * await emit({
 *   eventType: "sentiment_alert",
 *   payload: { score: -0.8, reason: "frustrated_keywords" },
 *   sentVia: "both",
 * });
 *
 * // Render received events (always [] in PRD 0)
 * return <ul>{events.map((e) => <li key={e.id}>{e.eventType}</li>)}</ul>;
 * ```
 *
 * Refs:
 * - Story 0.3.2: docs/MODULO_EXPANSAO_2026/stories/individual/0.3.2.md
 * - Story 0.3.1 (backend service): V1/backend/app/services/call_data_event_service.py
 * - ADR-006 (sent_via=both): docs/MODULO_EXPANSAO_2026/architecture/ARCH-PRD-0-foundation.md
 */

import { useCallback, useEffect, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";

/**
 * Transport channel(s) used to dispatch a call data event.
 *
 * Mirrors the Python `Literal["data_channel", "event_bus", "both"]` in
 * `CallDataEventService.emit()` (Story 0.3.1). Keep in sync with backend.
 *
 * - `data_channel`: LiveKit WebRTC data channel only (low latency,
 *   no persistence). Use for cursor overlay, ephemeral typing indicators.
 * - `event_bus`: backend event_bus only (persisted audit + rule_engine
 *   trigger, latency irrelevant). Use for watermark proof.
 * - `both`: data channel + event_bus (peer + audit + rule). Use for
 *   sentiment alerts, supervisor join, screen share start/stop.
 *
 * Audit row in `call_data_events` is **always** persisted on the
 * backend regardless of `sentVia` (ADR-006).
 */
export type SentVia = "data_channel" | "event_bus" | "both";

/**
 * Free-form JSONB payload, shape determined per `eventType`.
 *
 * Backend stores this as `call_data_events.payload` (jsonb). Schema
 * validation is the consumer's responsibility (each `eventType` has
 * its own contract; rigid schema would block evolution).
 */
export type CallDataEventPayload = Record<string, unknown>;

/**
 * Shape of a call data event as observed by subscribers.
 *
 * camelCase here — adapter at the network/LiveKit boundary (PRD 3) is
 * responsible for translating from the backend snake_case
 * (`call_session_id`, `actor_user_id`, etc.).
 */
export type CallDataEvent = {
  /** UUID of the audit row in `call_data_events`. */
  id: string;
  callSessionId: string;
  eventType: string;
  /** UUID of the user who triggered the event; `null` = system-emitted. */
  actorUserId: string | null;
  /** UUID of a specific recipient; `null` = broadcast to all participants. */
  targetUserId: string | null;
  payload: CallDataEventPayload;
  sentVia: SentVia;
  /** ISO 8601 timestamp from `call_data_events.created_at`. */
  createdAt: string;
};

/**
 * Arguments to `emit(...)`. Object form (not positional) because the
 * backend service uses keyword-only args; matching shape avoids
 * argument-ordering bugs across the boundary.
 *
 * `actorUserId` is intentionally absent — the backend resolves it from
 * the authenticated request context (PRD 3 will add an
 * `actor_user_id=Depends(get_current_user)` resolver).
 */
export type EmitArgs = {
  eventType: string;
  payload: CallDataEventPayload;
  /** Defaults to `"event_bus"` (audit + rule trigger, no peer broadcast). */
  sentVia?: SentVia;
  /** UUID of a specific recipient; omit for broadcast. */
  targetUserId?: string | null;
};

export type UseCallDataEventsResult = {
  /**
   * Events received over the LiveKit data channel since mount.
   *
   * **PRD 0:** always `[]` (stable reference — safe in `useEffect` deps).
   * **PRD 3:** populated via internal `useState`; new events appended in
   * arrival order.
   */
  events: ReadonlyArray<CallDataEvent>;
  /**
   * Emits a call data event.
   *
   * Resolves with the audit row UUID (`call_data_events.id`).
   *
   * **PRD 0:** logs `console.warn` and resolves with the sentinel UUID
   * `00000000-0000-0000-0000-000000000000` — no network, no broadcast.
   * **PRD 3:** dispatches via LiveKit / API per `sentVia` and returns
   * the real backend-assigned UUID.
   */
  emit: (args: EmitArgs) => Promise<string>;
};

/**
 * Stable empty array used as the `events` value in PRD 0. Module-level
 * constant so every call to the hook returns the **same reference** —
 * callers using `events` in `useEffect`/`useMemo` deps don't see a new
 * identity on every render and so don't loop.
 */
const EMPTY_EVENTS: ReadonlyArray<CallDataEvent> = Object.freeze([]);

/**
 * Sentinel UUID returned by the placeholder `emit`. Distinguishable
 * from any real backend UUID, which prevents bugs where a caller
 * persists the result and later sends a fake ID back to the server.
 */
const PLACEHOLDER_EVENT_ID = "00000000-0000-0000-0000-000000000000";

/**
 * Converte o corpo que o backend publica no canal de dados.
 *
 * O backend manda snake_case (`event_type`, `actor_user_id`); o contrato desta
 * camada é camelCase. Sem `id` a mensagem é descartada: ele é o que permite ao
 * consumidor não reexibir um aviso já dispensado, e sem isso o mesmo pedido
 * reaparece a cada reconexão da sala.
 *
 * Devolve `null` para qualquer coisa que não case — pacote de outra origem, de
 * uma versão futura, ou corrompido. Um evento malformado não pode derrubar a
 * chamada em andamento.
 */
export function converterEventoDoFio(bruto: unknown): CallDataEvent | null {
  if (typeof bruto !== "object" || bruto === null) return null;
  const o = bruto as Record<string, unknown>;
  const id = typeof o.id === "string" ? o.id : null;
  const eventType = typeof o.event_type === "string" ? o.event_type : null;
  if (!id || !eventType) return null;

  return {
    id,
    callSessionId: typeof o.call_session_id === "string" ? o.call_session_id : "",
    eventType,
    actorUserId: typeof o.actor_user_id === "string" ? o.actor_user_id : null,
    targetUserId: typeof o.target_user_id === "string" ? o.target_user_id : null,
    payload:
      typeof o.payload === "object" && o.payload !== null
        ? (o.payload as CallDataEventPayload)
        : {},
  } as CallDataEvent;
}

/**
 * Eventos da chamada, recebidos pelo canal de dados do LiveKit.
 *
 * **Deixou de ser placeholder em 07/08/2026.** Devolvia sempre `[]` e o `emit`
 * só fazia `console.warn`. Consequência prática: o pedido de compartilhamento
 * de tela do atendente era persistido, publicado — e o parceiro NUNCA via a
 * faixa, porque a lista de eventos era vazia por construção.
 *
 * Usa `useMaybeRoomContext`: fora de uma sala não há canal de dados, e devolver
 * lista vazia é a resposta honesta (não há como receber evento nenhum). Com
 * `useRoomContext` o hook lançaria — e ele é chamado também de telas que não
 * estão em chamada.
 */
export function useCallDataEvents(callSessionId: string): UseCallDataEventsResult {
  const sala = useMaybeRoomContext();
  const [events, setEvents] = useState<ReadonlyArray<CallDataEvent>>(EMPTY_EVENTS);

  useEffect(() => {
    if (!sala) return;

    const aoReceber = (dados: Uint8Array) => {
      let bruto: unknown;
      try {
        bruto = JSON.parse(new TextDecoder().decode(dados));
      } catch {
        return; // pacote que não é nosso — ignorar em silêncio é o certo aqui
      }
      const evento = converterEventoDoFio(bruto);
      if (!evento) return;

      setEvents((anteriores) =>
        // Deduplica por id: o LiveKit pode reentregar, e o consumidor usa o id
        // para lembrar o que já foi dispensado.
        anteriores.some((e) => e.id === evento.id) ? anteriores : [...anteriores, evento]
      );
    };

    sala.on(RoomEvent.DataReceived, aoReceber);
    return () => {
      sala.off(RoomEvent.DataReceived, aoReceber);
    };
  }, [sala]);

  const emit = useCallback(
    async (args: EmitArgs): Promise<string> => {
      // `emit` continua sem transporte próprio, e isso é DELIBERADO: quem
      // publica evento é o BACKEND (`CallDataEventService`), que persiste a
      // auditoria antes de publicar. Um cliente publicando direto no canal
      // criaria eventos sem trilha de auditoria — o oposto do que o ADR-006
      // exige. As telas usam endpoints REST dedicados (por exemplo
      // `atendimentosApi.requestScreenShare`).
      //
      // Nenhum chamador usa este `emit` hoje (conferido); ele existe para não
      // quebrar o contrato do tipo.
      // eslint-disable-next-line no-console
      console.warn(
        `[useCallDataEvents] emit() não publica: quem emite evento de chamada é o ` +
          `backend, que audita antes. session=${callSessionId} ` +
          `eventType=${args.eventType}`
      );
      return PLACEHOLDER_EVENT_ID;
    },
    [callSessionId]
  );

  return { events, emit };
}
